import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Calendar, Download, DollarSign, TrendingUp, Users, Send, CheckCircle, XCircle, Clock, Smartphone, Gift, Award, Wallet, Target } from 'lucide-react';
import { getFinancialDashboard, getDriverEarnings, getCompensations, getReferralPayouts } from '../../services/admin/financialService';
import { getAllUsers } from '../../services/admin/userService';
import { 
  initiateDisbursement, 
  checkMobileMoneyAccount, 
  getDriverDisbursements,
  getAllDisbursements,
  getDisbursementStatistics 
} from '../../services/admin/disbursementService';
import { toast } from '../../hooks/use-toast';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import QuestsTab from './QuestsTab';
import PromoFundTab from './PromoFundTab';

const FinancialPage = () => {
  const [financial, setFinancial] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverEarnings, setDriverEarnings] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'disbursements'
  
  // Disbursement state
  const [showDisbursementModal, setShowDisbursementModal] = useState(false);
  const [disbursementDriver, setDisbursementDriver] = useState(null);
  const [disbursementAmount, setDisbursementAmount] = useState('');
  const [disbursementProvider, setDisbursementProvider] = useState('airtel');
  const [disbursementNotes, setDisbursementNotes] = useState('');
  const [accountCheck, setAccountCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [disbursements, setDisbursements] = useState([]);
  const [disbursementStats, setDisbursementStats] = useState(null);

  // Compensations & Referral Payouts state
  const [compensations, setCompensations] = useState(null); // {items, total_amount, total_count}
  const [referralPayouts, setReferralPayouts] = useState(null);

  // Quick "Disburse Pending" modal state (Compensations tab)
  // Structure kept extensible for future enhancements:
  //  - breakdown: {base, comp, referral, lifetime_disbursed, pending_payout}
  //  - editable amount (admin can disburse partial)
  //  - provider toggle (airtel/tnm) + future bank/card support
  //  - notes for audit trail
  //  - accountChecked status (future: pre-flight account verification)
  const [quickDisbursement, setQuickDisbursement] = useState(null);
  const [quickLoading, setQuickLoading] = useState(false);
  const [quickSubmitting, setQuickSubmitting] = useState(false);

  useEffect(() => {
    loadFinancialData();
    loadDrivers();
    if (activeTab === 'disbursements') {
      loadDisbursements();
      loadDisbursementStats();
    }
    if (activeTab === 'compensations') {
      loadCompensations();
    }
    if (activeTab === 'referral_payouts') {
      loadReferralPayouts();
    }
  }, [dateRange, activeTab]);

  useEffect(() => {
    if (selectedDriver) {
      loadDriverEarnings();
    }
  }, [selectedDriver, period]);

  // Convert dateRange.end (YYYY-MM-DD) to inclusive end-of-day ISO so today's records show
  const apiDateRange = () => ({
    start: dateRange.start,
    end: dateRange.end ? `${dateRange.end}T23:59:59.999Z` : dateRange.end
  });

  const loadFinancialData = async () => {
    const { start, end } = apiDateRange();
    const result = await getFinancialDashboard(start, end);
    if (result.success) {
      setFinancial(result.data);
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
  };

  const loadDrivers = async () => {
    const result = await getAllUsers({ role: 'driver', limit: 1000 });
    if (result.success) {
      setDrivers(result.data.users);
    }
    setLoading(false);
  };

  const loadDriverEarnings = async () => {
    if (!selectedDriver) return;
    const result = await getDriverEarnings(selectedDriver, period);
    if (result.success) {
      setDriverEarnings(result.data);
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
  };

  const loadDisbursements = async () => {
    const result = await getAllDisbursements({ limit: 100 });
    if (result.success) {
      setDisbursements(result.data.disbursements);
    }
  };

  const loadDisbursementStats = async () => {
    const result = await getDisbursementStatistics(30);
    if (result.success) {
      setDisbursementStats(result.data);
    }
  };

  const loadCompensations = async () => {
    const { start, end } = apiDateRange();
    const result = await getCompensations(start, end);
    if (result.success) {
      setCompensations(result.data);
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
  };

  const loadReferralPayouts = async () => {
    const { start, end } = apiDateRange();
    const result = await getReferralPayouts(start, end);
    if (result.success) {
      setReferralPayouts(result.data);
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
  };

  // Quick "Disburse Pending" — opens prefilled modal with driver's pending payout
  const openQuickDisbursement = async (driverId, driverName) => {
    setQuickLoading(true);
    setQuickDisbursement({
      driverId,
      driverName,
      driverPhone: '',
      provider: 'airtel',
      amount: '',
      notes: '',
      breakdown: null,
      accountCheck: null,
    });

    const earnings = await getDriverEarnings(driverId, 'monthly');
    // Get phone via existing drivers list
    const driverRecord = drivers.find(d => d.id === driverId);
    const phone = driverRecord?.phone || '';
    // Auto-detect provider from phone prefix (Airtel: 099/098, TNM: 088/089)
    const cleaned = (phone || '').replace(/\D/g, '').replace(/^265/, '');
    const local = cleaned.length === 9 ? '0' + cleaned : cleaned;
    const detectedProvider = local.startsWith('088') || local.startsWith('089') ? 'tnm' : 'airtel';

    if (earnings.success) {
      const s = earnings.data.summary || {};
      setQuickDisbursement(prev => ({
        ...prev,
        driverPhone: phone,
        provider: detectedProvider,
        amount: String(s.pending_payout || 0),
        breakdown: {
          base: s.base_earnings || 0,
          comp: s.promo_compensations || 0,
          referral: s.referral_earnings || 0,
          lifetime_gross: s.lifetime_gross_earnings || 0,
          lifetime_disbursed: s.lifetime_disbursed || 0,
          pending_payout: s.pending_payout || 0,
        },
      }));
    } else {
      toast({ title: 'Error', description: earnings.message, variant: 'destructive' });
      setQuickDisbursement(null);
    }
    setQuickLoading(false);
  };

  const closeQuickDisbursement = () => {
    setQuickDisbursement(null);
    setQuickSubmitting(false);
  };

  const submitQuickDisbursement = async () => {
    if (!quickDisbursement) return;
    const amt = parseFloat(quickDisbursement.amount);
    if (isNaN(amt) || amt <= 0) {
      toast({ title: 'Invalid amount', description: 'Enter an amount greater than 0', variant: 'destructive' });
      return;
    }
    setQuickSubmitting(true);
    const result = await initiateDisbursement(
      quickDisbursement.driverId,
      amt,
      quickDisbursement.provider,
      quickDisbursement.notes || `Quick disburse from Compensations — ${new Date().toISOString()}`
    );
    setQuickSubmitting(false);
    if (result.success) {
      toast({ title: 'Disbursement initiated', description: `K${amt.toLocaleString()} sent to ${quickDisbursement.driverName}` });
      closeQuickDisbursement();
      loadCompensations();
      loadReferralPayouts();
      loadFinancialData();
    } else {
      toast({ title: 'Disbursement failed', description: result.message, variant: 'destructive' });
    }
  };

  const handleCheckAccount = async () => {
    if (!disbursementDriver) return;
    
    setChecking(true);
    const result = await checkMobileMoneyAccount(
      disbursementDriver.phone,
      disbursementProvider
    );
    setChecking(false);

    if (result.success) {
      setAccountCheck(result.data);
      if (!result.data.account_exists) {
        toast({ 
          title: 'Account Not Found', 
          description: 'Mobile money account does not exist or is inactive', 
          variant: 'destructive' 
        });
      } else {
        toast({ title: 'Account Verified', description: 'Mobile money account is active' });
      }
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
  };

  const handleInitiateDisbursement = async () => {
    if (!disbursementDriver || !disbursementAmount) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }

    const amount = parseFloat(disbursementAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({ title: 'Error', description: 'Invalid amount', variant: 'destructive' });
      return;
    }

    const result = await initiateDisbursement(
      disbursementDriver.id,
      amount,
      disbursementProvider,
      disbursementNotes || null
    );

    if (result.success) {
      toast({ title: 'Success', description: result.message });
      setShowDisbursementModal(false);
      setDisbursementDriver(null);
      setDisbursementAmount('');
      setDisbursementNotes('');
      setAccountCheck(null);
      loadDisbursements();
      loadDisbursementStats();
      loadDrivers(); // Refresh driver list to update pending earnings
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
  };

  const openDisbursementModal = (driver) => {
    setDisbursementDriver(driver);
    setShowDisbursementModal(true);
    setAccountCheck(null);
  };

  const exportToExcel = () => {
    if (!financial) return;

    const data = [
      ['Yachangu Financial Report'],
      [`Period: ${dateRange.start} to ${dateRange.end}`],
      [],
      ['Overview'],
      ['Total Rides', financial.overview.total_rides],
      ['Total Revenue', `K${financial.overview.total_revenue.toLocaleString()}`],
      ['Service Charges', `K${financial.overview.total_service_charges.toLocaleString()}`],
      ['Driver Earnings', `K${financial.overview.total_driver_earnings.toLocaleString()}`],
      [],
      ['By Vehicle Type'],
      ['Type', 'Rides', 'Revenue', 'Service Charges', 'Driver Earnings'],
    ];

    ['economy', 'premium', 'bike'].forEach(type => {
      const typeData = financial.by_vehicle_type[type];
      data.push([
        type.charAt(0).toUpperCase() + type.slice(1),
        typeData.count,
        `K${typeData.revenue.toLocaleString()}`,
        `K${typeData.service_charges.toLocaleString()}`,
        `K${typeData.driver_earnings.toLocaleString()}`
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Financial Report');
    XLSX.writeFile(wb, `yachangu_financial_${dateRange.start}_${dateRange.end}.xlsx`);

    toast({ title: 'Success', description: 'Report exported to Excel' });
  };

  const exportToPDF = () => {
    if (!financial) return;

    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.text('Yachangu Financial Report', 14, 20);
    doc.setFontSize(11);
    doc.text(`Period: ${dateRange.start} to ${dateRange.end}`, 14, 30);

    // Overview table
    doc.autoTable({
      startY: 40,
      head: [['Metric', 'Value']],
      body: [
        ['Total Rides', financial.overview.total_rides.toString()],
        ['Total Revenue', `K${financial.overview.total_revenue.toLocaleString()}`],
        ['Service Charges', `K${financial.overview.total_service_charges.toLocaleString()}`],
        ['Driver Earnings', `K${financial.overview.total_driver_earnings.toLocaleString()}`],
      ],
    });

    // By vehicle type
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.autoTable({
      startY: finalY,
      head: [['Vehicle Type', 'Rides', 'Revenue', 'Service Charges', 'Driver Earnings']],
      body: ['economy', 'premium', 'bike'].map(type => {
        const typeData = financial.by_vehicle_type[type];
        return [
          type.charAt(0).toUpperCase() + type.slice(1),
          typeData.count.toString(),
          `K${typeData.revenue.toLocaleString()}`,
          `K${typeData.service_charges.toLocaleString()}`,
          `K${typeData.driver_earnings.toLocaleString()}`
        ];
      }),
    });

    doc.save(`yachangu_financial_${dateRange.start}_${dateRange.end}.pdf`);
    toast({ title: 'Success', description: 'Report exported to PDF' });
  };

  const exportDriverEarningsToPDF = () => {
    if (!driverEarnings) return;

    const doc = new jsPDF();
    const driver = drivers.find(d => d.id === selectedDriver);
    
    doc.setFontSize(18);
    doc.text('Driver Earnings Report', 14, 20);
    doc.setFontSize(11);
    doc.text(`Driver: ${driver?.first_name} ${driver?.last_name}`, 14, 30);
    doc.text(`Period: ${period}`, 14, 36);

    doc.autoTable({
      startY: 45,
      head: [['Metric', 'Value']],
      body: [
        ['Total Rides', driverEarnings.summary.total_rides.toString()],
        ['Total Revenue', `K${driverEarnings.summary.total_revenue.toLocaleString()}`],
        ['Service Charges', `K${driverEarnings.summary.service_charges_deducted.toLocaleString()}`],
        ['Net Earnings', `K${driverEarnings.summary.net_earnings.toLocaleString()}`],
      ],
    });

    doc.save(`driver_earnings_${selectedDriver}_${period}.pdf`);
    toast({ title: 'Success', description: 'Driver report exported' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading financial data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Financial Management</h2>
          <p className="text-gray-600">Monitor revenue, earnings, and transactions</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700 flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Excel
          </Button>
          <Button onClick={exportToPDF} className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Date Range Filter */}
      <Card className="p-6">
        <div className="flex items-center gap-4">
          <Calendar className="w-5 h-5 text-gray-600" />
          <div className="flex-1 grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200 overflow-x-auto" data-testid="financial-tabs">
        {[
          { id: 'overview', label: 'Overview', icon: TrendingUp },
          { id: 'compensations', label: 'Promo Compensations', icon: Gift },
          { id: 'referral_payouts', label: 'Referral Payouts', icon: Award },
          { id: 'promo_fund', label: 'Promo Fund', icon: Wallet },
          { id: 'quests', label: 'Driver Quests', icon: Target },
        ].map(t => {
          const Icon = t.icon;
          const isActive = activeTab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                isActive
                  ? 'border-green-600 text-green-700'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
      <>
      {/* Financial Overview */}
      {financial && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <p className="text-sm text-gray-600">Total Rides</p>
              </div>
              <p className="text-3xl font-bold text-blue-700">{financial.overview.total_rides}</p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                <p className="text-sm text-gray-600">Total Revenue</p>
              </div>
              <p className="text-3xl font-bold text-green-700">
                K{financial.overview.total_revenue.toLocaleString()}
              </p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-purple-600" />
                <p className="text-sm text-gray-600">Service Charges</p>
              </div>
              <p className="text-3xl font-bold text-purple-700">
                K{financial.overview.total_service_charges.toLocaleString()}
              </p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-orange-50 to-orange-100">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-5 h-5 text-orange-600" />
                <p className="text-sm text-gray-600">Driver Earnings</p>
              </div>
              <p className="text-3xl font-bold text-orange-700">
                K{financial.overview.total_driver_earnings.toLocaleString()}
              </p>
            </Card>
          </div>

          {/* Platform Payouts (Compensations + Referral) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6 bg-gradient-to-br from-pink-50 to-pink-100" data-testid="overview-promo-comp-card">
              <div className="flex items-center gap-3 mb-2">
                <Gift className="w-5 h-5 text-pink-600" />
                <p className="text-sm text-gray-600">Promo Compensations</p>
              </div>
              <p className="text-3xl font-bold text-pink-700">
                K{(financial.overview.total_promo_compensations || 0).toLocaleString()}
              </p>
              <button
                onClick={() => setActiveTab('compensations')}
                className="text-xs text-pink-700 hover:underline mt-1"
              >
                View details →
              </button>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-indigo-50 to-indigo-100" data-testid="overview-referral-payout-card">
              <div className="flex items-center gap-3 mb-2">
                <Award className="w-5 h-5 text-indigo-600" />
                <p className="text-sm text-gray-600">Referral Payouts</p>
              </div>
              <p className="text-3xl font-bold text-indigo-700">
                K{(financial.overview.total_referral_payouts || 0).toLocaleString()}
              </p>
              <button
                onClick={() => setActiveTab('referral_payouts')}
                className="text-xs text-indigo-700 hover:underline mt-1"
              >
                View details →
              </button>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-amber-50 to-amber-100">
              <div className="flex items-center gap-3 mb-2">
                <Wallet className="w-5 h-5 text-amber-600" />
                <p className="text-sm text-gray-600">Total Platform Payouts</p>
              </div>
              <p className="text-3xl font-bold text-amber-700">
                K{(financial.overview.total_platform_payouts || 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-amber-700/70 mt-1">Compensations + Referral Bonuses</p>
            </Card>
          </div>

          {/* Vehicle Type Breakdown */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Revenue by Vehicle Type</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rides</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service Charges</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver Earnings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {['economy', 'premium', 'bike'].map((type) => {
                    const data = financial.by_vehicle_type[type];
                    return (
                      <tr key={type}>
                        <td className="px-6 py-4 whitespace-nowrap font-medium capitalize">{type}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{data.count}</td>
                        <td className="px-6 py-4 whitespace-nowrap">K{data.revenue.toLocaleString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap">K{data.service_charges.toLocaleString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap">K{data.driver_earnings.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* Driver Earnings Section */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Individual Driver Earnings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Select Driver</label>
            <select
              value={selectedDriver || ''}
              onChange={(e) => setSelectedDriver(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Choose a driver...</option>
              {drivers.map((driver) => (
                <option key={driver.id} value={driver.id}>
                  {driver.first_name} {driver.last_name} - {driver.phone}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Period</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly (7 days)</option>
              <option value="monthly">Monthly (30 days)</option>
              <option value="annual">Annual (365 days)</option>
            </select>
          </div>
        </div>

        {driverEarnings && (
          <div className="space-y-4" data-testid="driver-earnings-breakdown">
            {/* Headline cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4 bg-blue-50">
                <p className="text-sm text-gray-600">Total Rides</p>
                <p className="text-2xl font-bold text-blue-700">{driverEarnings.summary.total_rides}</p>
              </Card>
              <Card className="p-4 bg-green-50">
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-green-700">
                  K{driverEarnings.summary.total_revenue.toLocaleString()}
                </p>
              </Card>
              <Card className="p-4 bg-red-50">
                <p className="text-sm text-gray-600">Service Charges</p>
                <p className="text-2xl font-bold text-red-700">
                  K{driverEarnings.summary.service_charges_deducted.toLocaleString()}
                </p>
              </Card>
              <Card className="p-4 bg-purple-50">
                <p className="text-sm text-gray-600">Gross Earnings</p>
                <p className="text-2xl font-bold text-purple-700">
                  K{(driverEarnings.summary.gross_earnings || 0).toLocaleString()}
                </p>
              </Card>
            </div>

            {/* Earnings breakdown */}
            <Card className="p-5 bg-gradient-to-br from-gray-50 to-white border" data-testid="driver-earnings-equation">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Earnings Breakdown ({driverEarnings.period})</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <div className="bg-white rounded-lg p-3 border">
                  <p className="text-xs text-gray-500">Base (rides)</p>
                  <p className="text-lg font-bold text-gray-800">K{(driverEarnings.summary.base_earnings || 0).toLocaleString()}</p>
                </div>
                <div className="bg-pink-50 rounded-lg p-3 border border-pink-200" data-testid="driver-earnings-comp">
                  <p className="text-xs text-pink-700 flex items-center gap-1"><Gift className="w-3 h-3" /> Promo Compensations</p>
                  <p className="text-lg font-bold text-pink-700">K{(driverEarnings.summary.promo_compensations || 0).toLocaleString()}</p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200" data-testid="driver-earnings-referral">
                  <p className="text-xs text-indigo-700 flex items-center gap-1"><Award className="w-3 h-3" /> Referral Earnings</p>
                  <p className="text-lg font-bold text-indigo-700">K{(driverEarnings.summary.referral_earnings || 0).toLocaleString()}</p>
                </div>
              </div>
              <div className="text-xs text-gray-600 mb-3">
                Base K{(driverEarnings.summary.base_earnings || 0).toLocaleString()} + Compensations K{(driverEarnings.summary.promo_compensations || 0).toLocaleString()} + Referral K{(driverEarnings.summary.referral_earnings || 0).toLocaleString()} = <strong>Gross K{(driverEarnings.summary.gross_earnings || 0).toLocaleString()}</strong>
              </div>

              {/* Disbursement sync row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t" data-testid="driver-disbursement-sync">
                <div>
                  <p className="text-xs text-gray-500">Lifetime Gross</p>
                  <p className="text-base font-semibold text-gray-800">K{(driverEarnings.summary.lifetime_gross_earnings || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Lifetime Disbursed</p>
                  <p className="text-base font-semibold text-blue-700">K{(driverEarnings.summary.lifetime_disbursed || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pending Payout</p>
                  <p className="text-base font-bold text-amber-700" data-testid="driver-pending-payout">K{(driverEarnings.summary.pending_payout || 0).toLocaleString()}</p>
                </div>
              </div>
            </Card>

            {/* Per-ride compensation log */}
            {driverEarnings.compensations && driverEarnings.compensations.length > 0 && (
              <Card className="p-5">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Gift className="w-4 h-4 text-pink-600" /> Promo Compensations Log
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">When</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Original</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Rider Paid</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Discount %</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Compensation</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {driverEarnings.compensations.map(c => (
                        <tr key={c.ride_id}>
                          <td className="px-3 py-2 text-xs text-gray-600">{c.completed_at ? new Date(c.completed_at).toLocaleString() : '—'}</td>
                          <td className="px-3 py-2">K{(c.original_price || 0).toLocaleString()}</td>
                          <td className="px-3 py-2">K{(c.rider_paid || 0).toLocaleString()}</td>
                          <td className="px-3 py-2">{c.discount_percent || 0}%</td>
                          <td className="px-3 py-2 font-semibold text-pink-700">K{(c.compensation_amount || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Referral payouts log */}
            {driverEarnings.referral_payouts && driverEarnings.referral_payouts.length > 0 && (
              <Card className="p-5">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4 text-indigo-600" /> Referral Earnings Log
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">When</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Bonus</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {driverEarnings.referral_payouts.map(r => (
                        <tr key={r.credit_id}>
                          <td className="px-3 py-2 text-xs text-gray-600">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
                          <td className="px-3 py-2 text-xs">{r.description || '—'}</td>
                          <td className="px-3 py-2 font-semibold text-indigo-700">K{(r.bonus_amount || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            <Button
              onClick={exportDriverEarningsToPDF}
              className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Driver Report
            </Button>
          </div>
        )}
      </Card>
      </>
      )}

      {/* COMPENSATIONS TAB */}
      {activeTab === 'compensations' && (
        <Card className="p-6" data-testid="compensations-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Gift className="w-5 h-5 text-pink-600" /> Promo & Referral Compensations
              </h3>
              <p className="text-sm text-gray-600">Amounts the platform paid drivers to cover rider discounts</p>
            </div>
            {compensations && (
              <div className="text-right">
                <p className="text-xs text-gray-500">Total ({compensations.total_count} rides)</p>
                <p className="text-2xl font-bold text-pink-700" data-testid="compensations-total">K{(compensations.total_amount || 0).toLocaleString()}</p>
              </div>
            )}
          </div>
          {compensations && compensations.items && compensations.items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">When</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Driver</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Rider</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Original</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Rider Paid</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Disc %</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Compensation</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Pay Method</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {compensations.items.map(c => (
                    <tr key={c.ride_id} data-testid="compensation-row">
                      <td className="px-3 py-2 text-xs text-gray-600">{c.completed_at ? new Date(c.completed_at).toLocaleString() : '—'}</td>
                      <td className="px-3 py-2">{c.driver_name}</td>
                      <td className="px-3 py-2">{c.rider_name}</td>
                      <td className="px-3 py-2">K{(c.original_price || 0).toLocaleString()}</td>
                      <td className="px-3 py-2">K{(c.rider_paid || 0).toLocaleString()}</td>
                      <td className="px-3 py-2">{c.discount_percent || 0}%</td>
                      <td className="px-3 py-2 font-semibold text-pink-700">K{(c.compensation_amount || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-xs uppercase">{c.payment_method || '—'}</td>
                      <td className="px-3 py-2">
                        <button
                          data-testid={`disburse-pending-btn-${c.driver_id}`}
                          onClick={() => openQuickDisbursement(c.driver_id, c.driver_name)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition-colors"
                          title="Disburse this driver's pending payout"
                        >
                          <Send className="w-3 h-3" />
                          Disburse Pending
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Gift className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>No promo compensations in this period</p>
            </div>
          )}
        </Card>
      )}

      {/* REFERRAL PAYOUTS TAB */}
      {activeTab === 'referral_payouts' && (
        <Card className="p-6" data-testid="referral-payouts-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Award className="w-5 h-5 text-indigo-600" /> Referral Payouts
              </h3>
              <p className="text-sm text-gray-600">Bonuses paid to drivers/riders who referred a new user that completed their first trip</p>
            </div>
            {referralPayouts && (
              <div className="text-right">
                <p className="text-xs text-gray-500">Total ({referralPayouts.total_count} bonuses)</p>
                <p className="text-2xl font-bold text-indigo-700" data-testid="referral-payouts-total">K{(referralPayouts.total_amount || 0).toLocaleString()}</p>
              </div>
            )}
          </div>
          {referralPayouts && referralPayouts.items && referralPayouts.items.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">When</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Referrer</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Role</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Referee</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Bonus</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {referralPayouts.items.map(r => (
                    <tr key={r.credit_id} data-testid="referral-payout-row">
                      <td className="px-3 py-2 text-xs text-gray-600">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
                      <td className="px-3 py-2">{r.referrer_name}</td>
                      <td className="px-3 py-2 text-xs capitalize">{r.referrer_role || '—'}</td>
                      <td className="px-3 py-2">{r.referee_name || '—'}</td>
                      <td className="px-3 py-2 font-semibold text-indigo-700">K{(r.bonus_amount || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">{r.description || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Award className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>No referral payouts in this period</p>
            </div>
          )}
        </Card>
      )}

      {/* Quick Disburse Pending Modal */}
      {quickDisbursement && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          data-testid="quick-disburse-modal"
        >
          <div className="absolute inset-0 bg-black/50" onClick={closeQuickDisbursement} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <Send className="w-4 h-4 text-green-600" />
                  Disburse Pending — {quickDisbursement.driverName}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">{quickDisbursement.driverPhone || 'Loading…'}</p>
              </div>
              <button
                onClick={closeQuickDisbursement}
                className="p-1 hover:bg-gray-100 rounded"
                data-testid="quick-disburse-close-btn"
              >
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {quickLoading || !quickDisbursement.breakdown ? (
                <div className="py-10 text-center text-sm text-gray-500">
                  <Clock className="w-6 h-6 mx-auto animate-spin mb-2 text-green-600" />
                  Calculating pending payout…
                </div>
              ) : (
                <>
                  {/* Breakdown */}
                  <div className="bg-gray-50 rounded-lg p-3 border">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Earnings breakdown (lifetime)</p>
                    <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                      <div>
                        <div className="text-gray-500">Base</div>
                        <div className="font-semibold">K{(quickDisbursement.breakdown.base || 0).toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-pink-700">Compensations</div>
                        <div className="font-semibold text-pink-700">K{(quickDisbursement.breakdown.comp || 0).toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-indigo-700">Referral</div>
                        <div className="font-semibold text-indigo-700">K{(quickDisbursement.breakdown.referral || 0).toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t text-xs">
                      <span className="text-gray-600">Already disbursed</span>
                      <span className="font-medium">K{(quickDisbursement.breakdown.lifetime_disbursed || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1 text-sm">
                      <span className="text-gray-700 font-semibold">Pending payout</span>
                      <span className="font-bold text-amber-700" data-testid="quick-disburse-pending">
                        K{(quickDisbursement.breakdown.pending_payout || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* Provider toggle */}
                  <div>
                    <label className="text-xs font-semibold text-gray-700 mb-1.5 block">Mobile Money Provider</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['airtel', 'tnm'].map(p => (
                        <button
                          key={p}
                          data-testid={`quick-disburse-provider-${p}`}
                          onClick={() => setQuickDisbursement(prev => ({ ...prev, provider: p }))}
                          className={`py-2 rounded-lg border text-xs font-medium transition-colors ${
                            quickDisbursement.provider === p
                              ? p === 'airtel'
                                ? 'bg-red-50 border-red-500 text-red-700'
                                : 'bg-green-50 border-green-500 text-green-700'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {p === 'airtel' ? 'Airtel Money' : 'TNM Mpamba'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Editable amount (admin can disburse partial) */}
                  <div>
                    <label className="text-xs font-semibold text-gray-700 mb-1.5 block">Amount (MWK)</label>
                    <input
                      type="number"
                      value={quickDisbursement.amount}
                      onChange={(e) => setQuickDisbursement(prev => ({ ...prev, amount: e.target.value }))}
                      max={quickDisbursement.breakdown.pending_payout}
                      min={0}
                      data-testid="quick-disburse-amount-input"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">
                      Default = full pending payout. You can disburse partial.
                    </p>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="text-xs font-semibold text-gray-700 mb-1.5 block">Notes (optional)</label>
                    <textarea
                      value={quickDisbursement.notes}
                      onChange={(e) => setQuickDisbursement(prev => ({ ...prev, notes: e.target.value }))}
                      rows={2}
                      placeholder="Audit note for this disbursement…"
                      data-testid="quick-disburse-notes-input"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  {/* Future-enhancement placeholder slot — kept as a reserved area */}
                  {/* TODO: pre-flight account check, scheduling, multi-driver batch, bank/card support */}
                </>
              )}
            </div>

            <div className="px-5 py-4 border-t flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={closeQuickDisbursement}
                disabled={quickSubmitting}
                data-testid="quick-disburse-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={submitQuickDisbursement}
                disabled={quickSubmitting || quickLoading || !quickDisbursement.breakdown || (parseFloat(quickDisbursement.amount) || 0) <= 0}
                className="bg-green-600 hover:bg-green-700"
                data-testid="quick-disburse-confirm-btn"
              >
                {quickSubmitting ? 'Sending…' : `Disburse K${(parseFloat(quickDisbursement.amount) || 0).toLocaleString()}`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* DRIVER QUESTS TAB */}
      {activeTab === 'quests' && <QuestsTab />}
      {activeTab === 'promo_fund' && <PromoFundTab />}
    </div>
  );
};

export default FinancialPage;
